// Chatter Service - API calls for chatter functionality

export interface ChatterMessage {
  id: string
  opportunityId: string
  isoUserId: string
  senderType: 'iso' | 'client'
  senderName?: string
  senderEmail?: string
  messageText: string
  messageType: 'text' | 'file_request' | 'file_upload'
  fileAttachments: ChatterFileAttachment[]
  status: 'active' | 'deleted' | 'hidden'
  isRead: boolean
  notificationSent: boolean
  notificationSentAt?: string
  notificationType: 'new_message' | 'file_request' | 'mention'
  createdAt: string
  updatedAt: string
}

export interface ChatterFileAttachment {
  filename: string
  originalName: string
  mimeType: string
  size: number
  url?: string
}

export interface ChatterConversation {
  opportunityId: string
  businessName?: string
  totalMessages: number
  unreadMessages: number
  lastMessage?: {
    text: string
    senderName: string
    senderType: 'iso' | 'client'
    createdAt: string
  }
  lastActivity: string
}

export interface SendMessageData {
  message: string
  messageType?: 'text' | 'file_request' | 'file_upload'
  files?: File[]
}

class ChatterService {
  private baseUrl = '/api/chatter'

  /**
   * Get all chatter conversations for the current ISO
   */
  async getConversations(): Promise<ChatterConversation[]> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to get conversations: ${response.statusText}`)
      }

      const data = await response.json()
      return data.conversations || []
    } catch (error) {
      console.error('Error getting conversations:', error)
      throw error
    }
  }

  /**
   * Get messages for a specific opportunity
   */
  async getOpportunityMessages(opportunityId: string, limit = 50, offset = 0): Promise<{
    messages: ChatterMessage[]
    businessName?: string
    totalCount: number
  }> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      })

      const response = await fetch(`${this.baseUrl}/opportunity/${opportunityId}?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to get messages: ${response.statusText}`)
      }

      const data = await response.json()
      return {
        messages: data.messages || [],
        businessName: data.businessName,
        totalCount: data.totalCount || 0
      }
    } catch (error) {
      console.error('Error getting opportunity messages:', error)
      throw error
    }
  }

  /**
   * Send a message to an opportunity
   */
  async sendMessage(opportunityId: string, messageData: SendMessageData): Promise<ChatterMessage> {
    try {
      const formData = new FormData()
      formData.append('message', messageData.message)
      
      if (messageData.messageType) {
        formData.append('messageType', messageData.messageType)
      }

      // Add files if provided
      if (messageData.files && messageData.files.length > 0) {
        messageData.files.forEach((file) => {
          formData.append('files', file)
        })
      }

      const response = await fetch(`${this.baseUrl}/opportunity/${opportunityId}/message`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`)
      }

      const data = await response.json()
      return data.message
    } catch (error) {
      console.error('Error sending message:', error)
      throw error
    }
  }

  /**
   * Mark all messages as read for an opportunity
   */
  async markAsRead(opportunityId: string): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/opportunity/${opportunityId}/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to mark as read: ${response.statusText}`)
      }

      const data = await response.json()
      return data.markedCount || 0
    } catch (error) {
      console.error('Error marking as read:', error)
      throw error
    }
  }

  /**
   * Get total unread message count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/unread-count`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to get unread count: ${response.statusText}`)
      }

      const data = await response.json()
      return data.unreadCount || 0
    } catch (error) {
      console.error('Error getting unread count:', error)
      throw error
    }
  }

  /**
   * Create Server-Sent Events connection for real-time updates
   */
  createSSEConnection(opportunityId: string): EventSource {
    return new EventSource(`${this.baseUrl}/stream/${opportunityId}`, {
      withCredentials: true
    })
  }
}

export const chatterService = new ChatterService()
export default chatterService
